import DOMPurify from "isomorphic-dompurify";

/**
 * The one place the markup allowlist is expressed.
 *
 * Every `dangerouslySetInnerHTML` sink that renders a string from OUTSIDE this
 * interface goes through here. Markup literals written in our own source do not
 * need it (the `<style>` blocks in root.tsx, chart.tsx, client-hints.tsx and
 * api-docs._index.tsx are ours, and one of them carries a CSP nonce that
 * sanitising would strip).
 *
 * `isomorphic-dompurify` rather than `dompurify` because Remix renders these
 * sinks during SSR as well as on the client, and a browser-only sanitiser would
 * leave the server-rendered pass untouched -- the pass that puts the payload into
 * the HTML document before any client code runs.
 *
 * Keeping `<style>` and `style=` is deliberate: workflow output is often a
 * self-contained styled document, and rendering it as formatted content is the
 * feature. CSS can leak coarse information through attribute selectors, which is
 * a far smaller problem than the arbitrary script execution this removes.
 *
 * `form`, `object` and `embed` are allowed because the coder agent is *instructed*
 * to emit them, so forbidding them does not harden anything -- it deletes the
 * feature. `services/workflow-executor/agents/coder/prompt.ts` requires
 * `<form method="POST" target="_blank">` as the ROOT element of every human-input
 * form, puts the approve/reject endpoints in `formaction`, and names `<object>`
 * with `data` as the way to embed a document for review. Stripping them turns an
 * approval step into an inert page: the fields render, and nothing can be
 * submitted.
 *
 * `target` in particular must survive. `UserFormView` submits these forms itself
 * and cancels the submission before the browser acts, so the attribute is inert
 * on everything it handles — but on a submission it does NOT handle, such as an
 * "Escalate" button pointing somewhere of its own, `target="_blank"` is what
 * keeps a stray tab from becoming a navigation out of the app.
 *
 * What that costs, stated plainly: `object`/`embed` can point at any http(s) URL
 * and get a nested browsing context, and a `form` can post where its markup says.
 * The scheme check below stops `javascript:` and `data:` but cannot tell a review
 * document from attacker-controlled HTML at a URL that looks like ours. That
 * boundary belongs to whatever accepts the upload, not to a sanitiser -- which is
 * the honest reason these three are a product decision rather than a default.
 */
const CONFIG = {
  // `object` and `embed` are ADD_TAGS rather than merely absent from FORBID_TAGS
  // because DOMPurify's default allowlist does not contain them -- dropping them
  // from the forbid list alone leaves them still stripped, silently. `form` IS in
  // the default allowlist, so it only had to stop being forbidden.
  ADD_TAGS: ["style", "object", "embed"],
  // `data` (object) and `formaction` (the approve/reject buttons) are likewise not
  // in the default attribute allowlist, so the tag would survive stripped of the
  // one attribute that makes it do anything.
  ADD_ATTR: ["target", "data", "formaction", "formmethod"],
  // Parse the input as body content. Without this a leading `<style>` block is
  // hoisted into an implicit `<head>` and discarded, which silently renders
  // styled workflow output unstyled -- and `<style` is one of the signals
  // output-display.tsx uses to decide the content IS html in the first place.
  // It does not relax sanitising; `WHOLE_DOCUMENT` would keep the block too but
  // wraps the result in html/head/body, which is wrong for a fragment.
  FORCE_BODY: true,
  // Script execution vectors. `on*` handlers and `javascript:` / `data:` URLs are
  // removed by DOMPurify's own defaults, including in `data` and `formaction` --
  // it classes both as URI attributes, so they get the same scheme check as `href`
  // and `src` (measured, not assumed: `javascript:`, `data:text/html`,
  // `data:image/svg+xml` and entity-obfuscated `jav&#x09;ascript:` all come back
  // with the attribute gone).
  //
  // `iframe` stays out while `object` and `embed` are allowed, which looks
  // inconsistent and is not: nothing in the product emits an iframe, so allowing
  // it would buy a nested browsing context for no feature.
  FORBID_TAGS: ["script", "iframe", "base", "noscript"],
  // `srcdoc` would carry a whole document inline, and `ping` fires a background
  // request on click. Neither has a caller here.
  FORBID_ATTR: ["srcdoc", "ping"],
};

/**
 * Remove scripting constructs from untrusted markup, preserving the formatting the
 * content exists to convey. Returns a string safe to pass to
 * `dangerouslySetInnerHTML`.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, { ...CONFIG });
}
